import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { CodeSentToast } from '../../components/common/CodeSentToast';
import { PasswordInput } from '../../components/common/PasswordInput';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nickname: '',
    verificationCode: '',
    referralCode: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  const { register, sendVerificationCode, registerState, verificationState } = useAuthStore();

  // 从邀请链接携带的推荐码预填
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setFormData((f) => ({ ...f, referralCode: ref.toUpperCase() }));
  }, [searchParams]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (registerState === 'success') navigate('/');
  }, [registerState, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); setIsLoading(false); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); setIsLoading(false); return; }
    if (!formData.verificationCode) { setError('Please enter the verification code'); setIsLoading(false); return; }

    try {
      await register({
        email: formData.email,
        password: formData.password,
        nickname: formData.nickname,
        verificationCode: formData.verificationCode,
        referralCode: formData.referralCode || undefined,
      });
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (countdown > 0) return;
    if (!formData.email) { setError('Please enter your email'); return; }
    try {
      setError('');
      await sendVerificationCode({ email: formData.email, type: 'register' });
      setCountdown(60);
      setCodeSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send code');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 px-4">
      <CodeSentToast open={codeSent} onClose={() => setCodeSent(false)} />
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Sign up</h1>
          <p className="text-gray-600 mt-2">Create your account</p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input name="nickname" type="text" required className="input" placeholder="Your name" value={formData.nickname} onChange={handleChange} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input name="email" type="email" required className="input" placeholder="you@example.com" value={formData.email} onChange={handleChange} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <PasswordInput
                name="password"
                required
                autoComplete="new-password"
                placeholder="At least 6 characters"
                value={formData.password}
                onChange={(v) => setFormData((f) => ({ ...f, password: v }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
              <PasswordInput
                name="confirmPassword"
                required
                autoComplete="new-password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={(v) => setFormData((f) => ({ ...f, confirmPassword: v }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
              <div className="flex space-x-2">
                <input name="verificationCode" type="text" required className="input flex-1" placeholder="Enter the code" value={formData.verificationCode} onChange={handleChange} />
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={countdown > 0 || verificationState === 'loading'}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium min-w-[110px]"
                >
                  {countdown > 0 ? `${countdown}s` : 'Send code'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referral code (optional)</label>
              <input name="referralCode" type="text" className="input uppercase" placeholder="6-letter code" maxLength={6} value={formData.referralCode} onChange={handleChange} />
            </div>

            <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
              {isLoading ? 'Signing up…' : 'Sign up'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
